// src/redux/slices/uiSlice.js
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  onboarding: false,
  startingUp: true,
  negativeCredits: false,
  isBuyTarnOpen: false,
  isGeneralFeedbackOpen: false,
  isAccountSettingsOpen: false,

  /* NEW: track whether the Instructions dialog is open */
  isInstructionsOpen: false,

  reportButtonSelections: {},

  processingBatchUpdate: false,

  isTooManyRecordsOpen: false,
  aiBatchUpdateData: { messageId: null, batchUpdate: null },

  isAddAiFilterOpen: {
    isOpen: false,
    messageId: null,
    objectType: null,
  },

  runningAiFilter: {
    isRunning: false,
    messageId: null,
    progress: 0,
    status: 'complete',
  },

  runningAiAutofill: {
    isRunning: false,
    messageId: null,
    progress: 0,
    status: 'complete',
  },

  isAddAiFieldOpen: {
    isOpen: false,
    messageId: null,
    objectType: null,
    columns: null,
  },

  reportCardProgressBar: {
    isRunning: false,
    messageId: null,
    progress: 0,
    status: 'complete',
    progressExplainer: '',
  },

  selectedPackage: 'credits',

  askingSpecificAgent: {
    isAsking: false,
    messageId: null,
    agent:     null,
  },

  /* store columnOrder per messageId */
  columnOrders: {},

  columnAdded: {},

  /* NEW: track the “Send to Salesforce” modal/dialog state */
  isReportToSalesforceOpen: {
    isOpen: false,
    messageId: null,
    metacontent: null,
    objectType: null,
  },

  viewMode: 'dashboard',

  isTrailOpen: false,

  /* NEW: cross-surface highlight link (text ↔ visual) */
  hoverLink: { sourceMessageId: null, targetMessageId: null }, /* NEW */
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setOnboarding(state, action) {
      state.onboarding = action.payload;
    },
    setStartingUp(state, action) {
      state.startingUp = action.payload;
    },
    setNegativeCredits(state, action) {
      state.negativeCredits = action.payload;
    },
    setBuyTarnOpen(state, action) {
      state.isBuyTarnOpen = action.payload;
    },
    setIsGeneralFeedbackOpen(state, action) {
      state.isGeneralFeedbackOpen = action.payload;
    },
    setAccountSettingsOpen(state, action) {
      state.isAccountSettingsOpen = action.payload;
    },

    /* NEW: open/close the Instructions dialog */
    setInstructionsOpen(state, action) {
      state.isInstructionsOpen = action.payload;
    },

    setReportButtonSelection(state, action) {
      const { messageId, selection } = action.payload;
      state.reportButtonSelections[messageId] = selection;
    },
    clearReportButtonSelection(state, action) {
      const { messageId } = action.payload;
      delete state.reportButtonSelections[messageId];
    },
    setProcessingBatchUpdate(state, action) {
      state.processingBatchUpdate = action.payload;
    },
    setTooManyRecordsOpen(state, action) {
      state.isTooManyRecordsOpen = action.payload;
    },
    setAiBatchUpdateData(state, action) {
      state.aiBatchUpdateData = action.payload;
    },
    setAddAiFilterOpen(state, action) {
      const { isOpen, messageId, objectType } = action.payload;
      state.isAddAiFilterOpen.isOpen = isOpen;
      state.isAddAiFilterOpen.messageId = messageId;
      state.isAddAiFilterOpen.objectType = objectType;
    },
    setRunningAiFilter(state, action) {
      const { isRunning, messageId, progress, status } = action.payload;
      state.runningAiFilter.isRunning = isRunning;
      state.runningAiFilter.messageId = messageId;
      state.runningAiFilter.progress = progress;
      state.runningAiFilter.status = status;
    },
    setRunningAiAutofill(state, action) {
      const { isRunning, messageId, progress, status } = action.payload;
      state.runningAiAutofill.isRunning = isRunning;
      state.runningAiAutofill.messageId = messageId;
      state.runningAiAutofill.progress = progress;
      state.runningAiAutofill.status = status;
    },
    /* NEW: reducer for the unified progress bar */
    setReportCardProgressBar(state, action) {
      const { isRunning, messageId, progress, status, progressExplainer } = action.payload;
      state.reportCardProgressBar.isRunning = isRunning;
      state.reportCardProgressBar.messageId = messageId;
      state.reportCardProgressBar.progress = progress;
      state.reportCardProgressBar.status = status;
      state.reportCardProgressBar.progressExplainer = progressExplainer;
    },
    setSelectedPackage(state, action) {
      state.selectedPackage = action.payload;
    },
    setAskingSpecificAgent(state, action) {
      const { isAsking, messageId, agent } = action.payload;
      state.askingSpecificAgent.isAsking  = isAsking;
      state.askingSpecificAgent.messageId = messageId;
      state.askingSpecificAgent.agent     = agent;
    },
    /* NEW: store column order array under messageId */
    setColumnOrder(state, action) {
      const { messageId, columnOrder } = action.payload;
      state.columnOrders[messageId] = columnOrder;
    },
    setColumnAdded(state, action) {
      const { messageId, added } = action.payload;
      state.columnAdded[messageId] = added;
    },
    setAddAiFieldOpen(state, action) {
      const { isOpen, messageId, objectType, columns } = action.payload;
      state.isAddAiFieldOpen.isOpen = isOpen;
      state.isAddAiFieldOpen.messageId = messageId;
      state.isAddAiFieldOpen.objectType = objectType;
      state.isAddAiFieldOpen.columns = columns;
    },
    /* NEW: open/close the “Send to Salesforce” dialog */
    setReportToSalesforceOpen(state, action) {
      const { isOpen, messageId, metacontent, objectType } = action.payload;
      state.isReportToSalesforceOpen.isOpen = isOpen;
      state.isReportToSalesforceOpen.messageId = messageId;
      state.isReportToSalesforceOpen.metacontent = metacontent;
      state.isReportToSalesforceOpen.objectType = objectType;
    },

    setViewMode(state, action) {
      state.viewMode = action.payload; // expects 'thread' or 'dashboard'
    },

    setTrailOpen(state, action) {
      state.isTrailOpen = action.payload;
    },

    /* NEW: set/clear a single active hover link pair */
    setHoverLink(state, action) {                                   /* NEW */
      const { sourceMessageId, targetMessageId } = action.payload;   /* NEW */
      state.hoverLink = { sourceMessageId, targetMessageId };        /* NEW */
    },
    clearHoverLink(state) {                                          /* NEW */
      state.hoverLink = { sourceMessageId: null, targetMessageId: null };
    },                                                               /* NEW */
  },
});

export const {
  setOnboarding,
  setStartingUp,
  setNegativeCredits,
  setBuyTarnOpen,
  setIsGeneralFeedbackOpen,
  setAccountSettingsOpen,
  setReportButtonSelection,
  clearReportButtonSelection,
  setProcessingBatchUpdate,
  setTooManyRecordsOpen,
  setAiBatchUpdateData,
  setAddAiFilterOpen,
  setRunningAiFilter,
  setRunningAiAutofill,
  setReportCardProgressBar,
  setAskingSpecificAgent,
  setSelectedPackage,
  setColumnOrder,
  setColumnAdded,
  setAddAiFieldOpen,
  setReportToSalesforceOpen,
  setViewMode,
  setTrailOpen,
  /* NEW: export the Instructions action */
  setInstructionsOpen,
  /* NEW: export hover link actions */
  setHoverLink,                                                     /* NEW */
  clearHoverLink,                                                   /* NEW */
} = uiSlice.actions;

export default uiSlice.reducer;